import { PROFANITY_WORDS } from '../config/profanityWords';

export const REVIEW_STATUS = {
  ACTIVE: 'active',
  HIDDEN: 'hidden',
};

export const REVIEW_VOTE = {
  HELPFUL: 'helpful',
  UNHELPFUL: 'unhelpful',
};

export const NEW_ACCOUNT_DAYS = 7;
export const NEW_ACCOUNT_DAILY_LIMIT = 3;
export const REVIEW_TEXT_MAX_LEN = 2000;

export function createApiError(status, message) {
  const error = new Error(message);
  error.status = status;
  return error;
}

export function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

export function getUtcDayKey(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isNewAccount(createdAt, nowMs = Date.now(), days = NEW_ACCOUNT_DAYS) {
  const createdMs = toMillis(createdAt);
  if (!createdMs || !Number.isFinite(nowMs)) return false;
  return nowMs - createdMs < days * 24 * 60 * 60 * 1000;
}

export function canMutateByDailyLimit({ createdAt, dailyCount = 0, nowMs = Date.now() }) {
  const isNew = isNewAccount(createdAt, nowMs);
  const count = Number.isFinite(Number(dailyCount)) ? Number(dailyCount) : 0;
  return {
    isNewAccount: isNew,
    allowed: !isNew || count < NEW_ACCOUNT_DAILY_LIMIT,
    limit: NEW_ACCOUNT_DAILY_LIMIT,
    currentCount: count,
  };
}

export function normalizeReviewText(value) {
  if (value == null) return '';
  return String(value).trim();
}

export function findProfanity(text, dictionary = PROFANITY_WORDS) {
  const t = normalizeReviewText(text).toLowerCase();
  if (!t) return [];
  const hit = [];
  for (const word of dictionary) {
    const escaped = String(word).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\b${escaped}\\b`, 'i');
    if (re.test(t)) hit.push(word);
  }
  return hit;
}

export function validateReviewPayload({ rating, reviewText }) {
  const intRating = Number(rating);
  if (!Number.isInteger(intRating) || intRating < 1 || intRating > 5) {
    throw createApiError(400, 'Оцінка має бути цілим числом від 1 до 5.');
  }

  const text = normalizeReviewText(reviewText);
  if (text.length > REVIEW_TEXT_MAX_LEN) {
    throw createApiError(400, `Відгук не може перевищувати ${REVIEW_TEXT_MAX_LEN} символів.`);
  }

  const profanityHits = findProfanity(text);
  if (profanityHits.length > 0) {
    throw createApiError(400, 'Відгук містить неприйнятну лексику. Відредагуйте текст.');
  }

  return { rating: intRating, reviewText: text };
}

function safeIdPart(value) {
  return String(value || '')
    .trim()
    .replace(/[\\/#?[\]]/g, '_')
    .replace(/\s+/g, '_');
}

export function buildReviewId(bookId, userId) {
  const b = safeIdPart(bookId);
  const u = safeIdPart(userId);
  if (!b || !u) throw createApiError(400, 'Невірний ключ review.');
  return `${b}__${u}`;
}

export function isCompletedBorrowRecord(row = {}) {
  const status = String(row.status || row.borrowStatus || '').toLowerCase().trim();
  const action = String(row.action || '').toLowerCase().trim();
  return (
    status === 'completed' ||
    status === 'returned' ||
    status === 'picked_up' ||
    action === 'return' ||
    action === 'take' ||
    action === 'add'
  );
}

export function hasVerifiedBorrowRecord(rows = []) {
  return Array.isArray(rows) && rows.some((r) => isCompletedBorrowRecord(r));
}

function toAverage(sum, count) {
  if (!count) return null;
  return Number((sum / count).toFixed(2));
}

export function computeReviewAggregates(reviews = []) {
  let allSum = 0;
  let allCount = 0;
  let verifiedSum = 0;
  let verifiedCount = 0;

  for (const review of reviews) {
    if ((review?.status || REVIEW_STATUS.ACTIVE) !== REVIEW_STATUS.ACTIVE) continue;
    const rating = Number(review?.rating);
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) continue;

    allSum += rating;
    allCount += 1;

    if (Boolean(review?.isVerifiedReader)) {
      verifiedSum += rating;
      verifiedCount += 1;
    }
  }

  return {
    averageRatingAll: toAverage(allSum, allCount),
    ratingsCountAll: allCount,
    averageRatingVerified: toAverage(verifiedSum, verifiedCount),
    ratingsCountVerified: verifiedCount,
  };
}

export function applyVoteTransition({
  currentVote = null,
  nextVote = null,
  helpfulCount = 0,
  unhelpfulCount = 0,
}) {
  let helpful = Math.max(0, Number(helpfulCount) || 0);
  let unhelpful = Math.max(0, Number(unhelpfulCount) || 0);

  if (currentVote === REVIEW_VOTE.HELPFUL) helpful = Math.max(0, helpful - 1);
  if (currentVote === REVIEW_VOTE.UNHELPFUL) unhelpful = Math.max(0, unhelpful - 1);

  if (nextVote === REVIEW_VOTE.HELPFUL) helpful += 1;
  if (nextVote === REVIEW_VOTE.UNHELPFUL) unhelpful += 1;

  return { helpfulCount: helpful, unhelpfulCount: unhelpful };
}
